import { useState } from 'react';
import type { DiagnosticView } from '@shared/prospect-diagnostic';
import { DIAGNOSTIC_CONTACT_URL, type LeadResult } from '@/lib/lead-diagnostic';
import { DiagnosticResultView } from '@/components/diagnostic/DiagnosticResultView';
import { QuestionTitle, diagnosticPrimary } from '@/components/diagnostic/DiagnosticParts';
import { DiagnosticWriting } from '@/components/diagnostic/DiagnosticWriting';

const ContactButton = () => (
  <a href={DIAGNOSTIC_CONTACT_URL} target="_blank" rel="noreferrer" className={`${diagnosticPrimary} self-start`}>
    Améliorer ce plan avec nous
  </a>
);

const secondaryButton =
  'flex min-h-[52px] items-center rounded-full border-2 border-domino-sand bg-white px-7 text-base font-semibold text-domino-aubergine disabled:opacity-50';

/** P3: the diagnostic sent to the verified address it belongs to. */
const EmailButton = ({ onEmail }: { onEmail: () => Promise<LeadResult<{ sentTo: string }>> }) => {
  const [state, setState] = useState<{ busy: boolean; note: string | null }>({ busy: false, note: null });
  const send = async () => {
    setState({ busy: true, note: null });
    const result = await onEmail();
    setState({ busy: false, note: result.ok ? `Envoyé à ${result.sentTo}` : result.message });
  };
  return (
    <div className="flex flex-col gap-2">
      <button type="button" onClick={() => void send()} disabled={state.busy} className={secondaryButton}>Recevoir par e-mail</button>
      {state.note && <p role="status" className="text-[14px] font-semibold text-domino-inkSoft">{state.note}</p>}
    </div>
  );
};

interface Props {
  diagnostic: DiagnosticView;
  onRetry: () => void;
  retrying: boolean;
  onEmail: () => Promise<LeadResult<{ sentTo: string }>>;
}

/** After the identity check: the diagnostic being written, failed, or ready. */
export const DiagnosticReady = ({ diagnostic, onRetry, retrying, onEmail }: Props) => {
  if (diagnostic.status === 'ready' && diagnostic.result) {
    return (
      <>
        <DiagnosticResultView result={diagnostic.result} />
        <div className="flex flex-wrap items-start gap-3">
          <ContactButton />
          <EmailButton onEmail={onEmail} />
        </div>
      </>
    );
  }

  if (diagnostic.status === 'failed') {
    return (
      <>
        <QuestionTitle>Le résultat n’a pas pu s’écrire.</QuestionTitle>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={onRetry} disabled={retrying} className={diagnosticPrimary}>Relancer</button>
          <a href={DIAGNOSTIC_CONTACT_URL} target="_blank" rel="noreferrer" className={secondaryButton}>
            Contacter Domino
          </a>
        </div>
      </>
    );
  }

  // Pending or generating: the Forge sun as a loader and what Domino weighs (Benoit, 03/11).
  return <DiagnosticWriting answers={diagnostic.answers} />;
};
